import { useCallback, useRef } from 'react';

import { salesApi } from '../../services/salesApi.js';

/**
 * Shared price-list resolution for the quote/order entry modals.
 *
 * Fire-and-forget: calls `GET /sales/price-lists/resolve` and patches the
 * matching form line when a price-list tier applies. Errors stay silent -
 * the caller's synchronous listPrice fallback keeps working unchanged.
 *
 * Race guard (last request wins): each line index keeps a monotonically
 * increasing token; a response is only applied when its token still matches
 * the latest request for that line. `invalidate()` drops every in-flight
 * response (call it when line indices shift, e.g. after removing a line).
 *
 * @param {(updater: (prev: object) => object) => void} setForm
 *   React state setter for a form shaped `{ lines: [{ productId, quantity, unitPrice, priceHint }] }`.
 */
export function usePriceResolver(setForm) {
  const tokensRef = useRef({});

  const invalidate = useCallback(() => {
    tokensRef.current = {};
  }, []);

  const resolveLine = useCallback(
    (idx, { customerId, productId, quantity }) => {
      if (!customerId || !productId || !(Number(quantity) > 0)) {
        return;
      }
      const token = (tokensRef.current[idx] || 0) + 1;
      tokensRef.current[idx] = token;

      salesApi
        .resolvePrice({ customerId, productId, quantity: Number(quantity) })
        .then(({ items }) => {
          if (tokensRef.current[idx] !== token) {
            return; // a newer request for this line is in flight / already applied
          }
          setForm((p) => ({
            ...p,
            lines: p.lines.map((line, i) => {
              if (i !== idx || line.productId !== productId) {
                return line;
              }
              if (items?.source === 'PRICE_LIST' && items.unitPrice != null) {
                return {
                  ...line,
                  unitPrice: String(Number(items.unitPrice)),
                  priceHint: {
                    name: items.priceListName,
                    minQuantity: String(Number(items.minQuantity)),
                  },
                };
              }
              // NONE: never touch the price (manual edits / listPrice fallback stay),
              // only drop a stale hint.
              return line.priceHint ? { ...line, priceHint: null } : line;
            }),
          }));
        })
        .catch(() => {
          // Silent by design: resolve failures must never break order entry.
        });
    },
    [setForm],
  );

  /** Clears all hints (e.g. on customer change) without touching prices. */
  const clearHints = useCallback(() => {
    tokensRef.current = {};
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line) => (line.priceHint ? { ...line, priceHint: null } : line)),
    }));
  }, [setForm]);

  return { resolveLine, clearHints, invalidate };
}
